/**
 * Notification Sound Utility
 * Plays a short notification tone when a new chat message arrives.
 *
 * Uses the Web Audio API to synthesize a pleasant two-tone "ding" –
 * no external audio files required.
 *
 * Features:
 * - Throttled: won't play more than once every 1.5 seconds (avoid spam in group chats)
 * - Respects user mute preference stored in localStorage
 * - Gracefully degrades when Audio API is unavailable
 */

let audioContext = null;
let lastPlayedAt = 0;

const THROTTLE_MS = 1500; // Min gap between notification sounds
const STORAGE_KEY = 'chat_notification_sound_enabled';

/**
 * Get or create a shared AudioContext (lazy singleton).
 * Must be called after a user gesture for browsers that require it.
 */
const getAudioContext = () => {
  if (!audioContext || audioContext.state === 'closed') {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    audioContext = new AudioCtx();
  }
  // Resume if suspended (Safari / Chrome autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

/**
 * Synthesise and play a short two-tone notification "ding".
 * Tone 1: 880 Hz (A5) for 80 ms → Tone 2: 1174 Hz (D6) for 100 ms
 */
const playTone = () => {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // --- Gain envelope ---
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0, now);
  // Tone 1: quick ramp up
  gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
  gain.gain.linearRampToValueAtTime(0.12, now + 0.08);
  // Tone 2
  gain.gain.linearRampToValueAtTime(0.15, now + 0.1);
  gain.gain.linearRampToValueAtTime(0, now + 0.25);

  // --- Tone 1: 880 Hz ---
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(880, now);
  osc1.connect(gain);
  osc1.start(now);
  osc1.stop(now + 0.1);

  // --- Tone 2: 1174 Hz (D6) ---
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1174.66, now + 0.1);
  osc2.connect(gain);
  osc2.start(now + 0.1);
  osc2.stop(now + 0.25);
};

/**
 * Check whether notification sounds are enabled.
 * Defaults to `true` if no preference has been stored.
 */
export const isNotificationSoundEnabled = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

/**
 * Toggle notification sound on/off and persist the preference.
 * @param {boolean} enabled
 */
export const setNotificationSoundEnabled = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(!!enabled));
  } catch {
    // localStorage unavailable – silently ignore
  }
};

/**
 * Play the notification sound if allowed.
 * Respects mute preference and throttle window.
 */
export const playNotificationSound = () => {
  if (!isNotificationSoundEnabled()) return;

  const now = Date.now();
  if (now - lastPlayedAt < THROTTLE_MS) return;
  lastPlayedAt = now;

  try {
    playTone();
  } catch (err) {
    // Web Audio not available – silently ignore
    console.warn('Notification sound failed:', err);
  }
};

/**
 * Resume the AudioContext after a user gesture.
 * Call this once (e.g. on first click/keydown) to unlock audio on
 * browsers with strict autoplay policies.
 */
export const unlockAudio = () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
};
